import React, { useContext, useEffect, useState } from "react";
import { ReportParamsContext } from "../../context/ReportParamsContext";
import { getEntities, downloadRetailPriceDetail } from "../../API";
import DropdownMultiSelect from "../../components/DropdownMultiSelect";

function normalizeResponse(response) {
  if (Array.isArray(response)) return response;
  if (response?._embedded) return Object.values(response._embedded).flat();
  if (response?.content) return response.content;
  return [];
}

export default function RetailPriceDetailReport() {
  const [{ manufacturer, fromDate, toDate, representatives, itemStatus: itemStatusRaw }] =
    useContext(ReportParamsContext);
  const itemStatus = itemStatusRaw ?? "ACTIVE";

  const [banners, setBanners] = useState([]);
  const [selectedBanners, setSelectedBanners] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    getEntities("banners", "?size=1000").then((r) =>
      setBanners(normalizeResponse(r).sort((a, b) => a.name.localeCompare(b.name)))
    );
  }, []);

  useEffect(() => {
    const mfrId = manufacturer?.id;
    setSelectedProducts([]);
    if (!mfrId) { setProducts([]); return; }
    setProductsLoading(true);
    const statusSearch = itemStatus === "ALL" ? "" : `,status:${itemStatus}`;
    getEntities("products", `?search=manufacturer:${mfrId}${statusSearch}&size=1000`)
      .then((res) => setProducts(normalizeResponse(res)))
      .finally(() => setProductsLoading(false));
  }, [manufacturer?.id, itemStatus]);

  const canGenerate = !!(manufacturer && fromDate && toDate && selectedProducts.length > 0);

  const handleDownload = async () => {
    if (!canGenerate) return;
    setDownloading(true);
    setError(null);
    try {
      await downloadRetailPriceDetail({
        manufacturerId: manufacturer.id,
        fromDate,
        toDate,
        bannerIds: selectedBanners.map((b) => b.id),
        representativeIds: (representatives ?? []).map((r) => r.id),
        productIds: selectedProducts.map((p) => p.id),
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex-grow-1 p-4 overflow-auto">
      <h5 className="mb-4">Retail Price Detail</h5>

      <div className="row g-3 mb-4 align-items-end">
        <div className="col-md-3">
          <label className="form-label small mb-1">Banners</label>
          <DropdownMultiSelect
            options={banners}
            value={selectedBanners}
            onChange={setSelectedBanners}
            getOptionLabel={(o) => o.name}
            getOptionValue={(o) => o.id}
            placeholder="All banners"
          />
        </div>
        <div className="col-md-3">
          <label className="form-label small mb-1">
            Products{productsLoading ? " (loading…)" : ""}
          </label>
          <DropdownMultiSelect
            options={products}
            value={selectedProducts}
            onChange={setSelectedProducts}
            getOptionLabel={(o) => [o.description, o.formatQut, o.format].filter(Boolean).join(" ")}
            getOptionValue={(o) => o.id}
            placeholder="Select products..."
            disabled={!manufacturer || productsLoading}
          />
        </div>
      </div>

      {!manufacturer && (
        <p className="text-muted small mb-3">Select a manufacturer in the panel above.</p>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      <button className="btn btn-primary" onClick={handleDownload} disabled={!canGenerate || downloading}>
        {downloading ? "Generating…" : "Download Excel"}
      </button>
    </div>
  );
}
